import type { Store } from '@blocksuite/affine/store';
import { createDatabaseBlockDataSource } from '@blocksuite/affine-block-database';

import type {
  AutomationDatabaseBindings,
  AutomationDatabaseSurfaceBinding,
} from './automation-database-bindings';
import {
  collectDatabaseShardDocIds,
  collectDatabaseSystemDocIdsByPageDoc,
} from './automation-install-options';

type BlockLike = {
  readonly model: {
    readonly id: string;
    readonly props: Record<string, unknown>;
  };
};

interface ImportedDatabaseRef {
  readonly docId: string | null;
  readonly tableId: string | null;
}

type DataSourceLike = {
  readonly ['schemaLocked$']?: { readonly value?: unknown };
  propertyAdd(
    position: 'start' | 'end',
    options: { type: string; name: string }
  ): string | undefined;
  propertyMetaGet?(type: string): unknown;
  propertyNameSet(propertyId: string, name: string): void;
  propertyTypeGet(propertyId: string): string | undefined;
};

export function applyAutomationDatabaseBindingImportOptions(
  stores: readonly Store[],
  bindings: AutomationDatabaseBindings | undefined,
  removeDoc: (docId: string) => void
): readonly string[] {
  if (!bindings) return [];

  const removed = new Set<string>();
  const systemDocIdsByPageDoc = collectDatabaseSystemDocIdsByPageDoc(stores);
  const systemDocIdsByBlock = collectDatabaseSystemDocIdsByBlock(stores);
  const storesById = new Map(stores.map(store => [store.id, store]));

  for (const ref of collectSurfaceRefs(stores)) {
    for (const surface of bindings.surfaces) {
      if (surface.mode !== 'use_existing') continue;
      const importedRef = getSurfaceImportedRef(ref, surface);
      if (!importedRef.docId) continue;
      removeImportedDatabaseRef({
        importedRef,
        label: surface.databaseLabel,
        removed,
        storesById,
        systemDocIdsByBlock,
      });
    }
  }

  for (const docId of Array.from(removed)) {
    const systemDocIds = systemDocIdsByPageDoc.get(docId);
    if (!systemDocIds) continue;
    for (const systemDocId of systemDocIds) removed.add(systemDocId);
  }

  for (const docId of removed) removeDoc(docId);
  return Array.from(removed);
}

export function resolveAutomationDatabaseBindings(
  targetDoc: Store,
  bindings: AutomationDatabaseBindings | undefined
): AutomationDatabaseBindings | undefined {
  if (!bindings) return undefined;

  return {
    ...bindings,
    surfaces: bindings.surfaces.map(surface => {
      if (surface.mode !== 'use_existing') return surface;
      const databaseBlockId = surface.targetDatabaseBlockId;
      if (!databaseBlockId) {
        throw new Error(
          `Existing database binding for "${surface.databaseLabel}" has no target database.`
        );
      }

      const databaseBlock = targetDoc.getBlock(databaseBlockId) as
        | { model?: unknown }
        | undefined;
      const model = databaseBlock?.model;
      if (!model) {
        throw new Error(
          `Target database "${surface.targetDatabaseName ?? databaseBlockId}" is not available in the selected document.`
        );
      }

      const dataSource = createDatabaseBlockDataSource(model as never) as
        | DataSourceLike
        | undefined;
      if (!dataSource) {
        throw new Error(
          `Target database "${surface.targetDatabaseName ?? databaseBlockId}" cannot be edited.`
        );
      }

      const existingNames = collectDatabaseColumnNames(model);
      for (const column of surface.columns) {
        if (column.targetColumnName) existingNames.add(column.targetColumnName);
      }

      return {
        ...surface,
        targetDocId: targetDoc.id,
        columns: surface.columns.map(column => {
          if (column.action === 'map_existing') {
            if (!column.targetColumnId) {
              throw new Error(
                `Column "${column.templateColumnName}" has no target column.`
              );
            }
            return column;
          }

          const propertyType = toDatabasePropertyType(
            column.templateColumnType
          );
          if (isDatabaseSchemaLocked(model, dataSource)) {
            throw new Error(
              `Column "${column.templateColumnName}" cannot be created because target database "${surface.targetDatabaseName ?? databaseBlockId}" has a locked schema.`
            );
          }
          if (!supportsDatabasePropertyType(dataSource, propertyType)) {
            throw new Error(
              `Column "${column.templateColumnName}" cannot be created because database property type "${propertyType}" is not available.`
            );
          }
          const columnName = uniqueName(
            column.templateColumnName,
            existingNames
          );
          const newColumnId = dataSource.propertyAdd('end', {
            type: propertyType,
            name: columnName,
          });
          if (!newColumnId) {
            throw new Error(
              `Column "${column.templateColumnName}" could not be created in target database "${surface.targetDatabaseName ?? databaseBlockId}".`
            );
          }
          dataSource.propertyNameSet(newColumnId, columnName);
          existingNames.add(columnName);
          return {
            ...column,
            targetColumnId: newColumnId,
            targetColumnName: columnName,
            targetColumnType:
              dataSource.propertyTypeGet(newColumnId) ?? propertyType,
          };
        }),
      };
    }),
  };
}

function collectSurfaceRefs(stores: readonly Store[]) {
  const refs: Array<{
    source: ImportedDatabaseRef;
    inputRefs: readonly ImportedDatabaseRef[];
    outputRefs: readonly ImportedDatabaseRef[];
  }> = [];

  for (const store of stores) {
    for (const block of getAutomationBlocks(store)) {
      const props = block.model.props;
      refs.push({
        source: {
          docId: readString(props.sourceDocId),
          tableId: readString(props.sourceTableId),
        },
        inputRefs: readTableRefs(props.inputTables),
        outputRefs: readTableRefs(props.outputTables),
      });
    }
  }

  return refs;
}

function getSurfaceImportedRef(
  ref: {
    source: ImportedDatabaseRef;
    inputRefs: readonly ImportedDatabaseRef[];
    outputRefs: readonly ImportedDatabaseRef[];
  },
  surface: AutomationDatabaseSurfaceBinding
): ImportedDatabaseRef {
  const index = Math.max(0, surface.databaseIndex);
  if (surface.databaseRole === 'input') {
    return ref.inputRefs[index] ?? ref.source;
  }
  if (surface.databaseRole === 'output') {
    return ref.outputRefs[index] ?? { docId: null, tableId: null };
  }
  return { docId: null, tableId: null };
}

function removeImportedDatabaseRef({
  importedRef,
  label,
  removed,
  storesById,
  systemDocIdsByBlock,
}: {
  importedRef: ImportedDatabaseRef;
  label: string;
  removed: Set<string>;
  storesById: ReadonlyMap<string, Store>;
  systemDocIdsByBlock: ReadonlyMap<string, ReadonlyMap<string, Set<string>>>;
}): void {
  const store = importedRef.docId ? storesById.get(importedRef.docId) : null;
  if (!store || !containsAutomationBlock(store)) {
    if (importedRef.docId) removed.add(importedRef.docId);
    return;
  }

  if (!importedRef.tableId) {
    throw new Error(
      `Existing database binding for "${label}" cannot identify the template database block.`
    );
  }

  const databaseBlock = getDatabaseBlocks(store).find(
    block => block.model.id === importedRef.tableId
  );
  if (!databaseBlock) {
    throw new Error(
      `Existing database binding for "${label}" cannot find template database block "${importedRef.tableId}".`
    );
  }

  const systemDocIds = systemDocIdsByBlock
    .get(store.id)
    ?.get(importedRef.tableId);
  for (const systemDocId of systemDocIds ?? []) {
    removed.add(systemDocId);
  }

  store.transact(() => {
    store.deleteBlock(
      databaseBlock.model as Parameters<Store['deleteBlock']>[0]
    );
  });
}

function containsAutomationBlock(store: Store): boolean {
  return getAutomationBlocks(store).length > 0;
}

function getAutomationBlocks(store: Store): BlockLike[] {
  return store.getBlocksByFlavour(
    'affine:automation'
  ) as unknown as BlockLike[];
}

function getDatabaseBlocks(store: Store): BlockLike[] {
  return store.getBlocksByFlavour('affine:database') as unknown as BlockLike[];
}

function readTableRefs(value: unknown): readonly ImportedDatabaseRef[] {
  if (!Array.isArray(value)) return [];
  const refs: ImportedDatabaseRef[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as { docId?: unknown; tableId?: unknown };
    const docId = readString(record.docId);
    if (!docId) continue;
    refs.push({
      docId,
      tableId: readString(record.tableId),
    });
  }
  return refs;
}

function collectDatabaseSystemDocIdsByBlock(
  stores: readonly Store[]
): Map<string, Map<string, Set<string>>> {
  const systemDocIdsByBlock = new Map<string, Map<string, Set<string>>>();
  const storesById = new Map(stores.map(store => [store.id, store]));

  for (const store of stores) {
    for (const block of getDatabaseBlocks(store)) {
      const props = block.model.props;
      const rootDocId = readString(props.databaseRootDocId);
      if (!rootDocId) continue;

      const blockSystemDocs = new Set<string>([rootDocId]);
      const rootStore = storesById.get(rootDocId);
      if (rootStore) {
        for (const shardDocId of collectDatabaseShardDocIds(rootStore)) {
          blockSystemDocs.add(shardDocId);
        }
      }

      const byBlock = systemDocIdsByBlock.get(store.id) ?? new Map();
      byBlock.set(block.model.id, blockSystemDocs);
      systemDocIdsByBlock.set(store.id, byBlock);
    }
  }

  return systemDocIdsByBlock;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function toDatabasePropertyType(type: string): string {
  if (type === 'rich-text') return 'text';
  if (type === 'title') return 'text';
  return type;
}

function isDatabaseSchemaLocked(
  model: unknown,
  dataSource: DataSourceLike
): boolean {
  const modelLocked =
    (model as { props?: { schemaLocked?: unknown } }).props?.schemaLocked ===
    true;
  return modelLocked || dataSource['schemaLocked$']?.value === true;
}

function supportsDatabasePropertyType(
  dataSource: DataSourceLike,
  type: string
): boolean {
  if (!dataSource.propertyMetaGet) return true;
  return dataSource.propertyMetaGet(type) !== undefined;
}

function collectDatabaseColumnNames(model: unknown): Set<string> {
  const columns = (model as { props?: { columns?: unknown } }).props?.columns;
  if (!Array.isArray(columns)) return new Set();
  return new Set(
    columns
      .map(column =>
        column && typeof column === 'object'
          ? (column as { name?: unknown }).name
          : undefined
      )
      .filter((name): name is string => typeof name === 'string')
  );
}

function uniqueName(name: string, existingNames: Set<string>): string {
  const trimmed = name.trim() || 'Column';
  if (!existingNames.has(trimmed)) return trimmed;
  let index = 2;
  while (existingNames.has(`${trimmed} ${index}`)) index++;
  return `${trimmed} ${index}`;
}
